import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, Bird, Handshake, X, Mail, Phone, MapPin, ChevronDown, ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import './Homepage.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ───── Splash Screen ───── */
const SplashScreen = ({ onDone }) => {
  useEffect(() => { const t = setTimeout(onDone, 1600); return () => clearTimeout(t); }, [onDone]);
  const [broken, setBroken] = useState(false);
  return (
    <div className="hp-splash" data-testid="splash-screen">
      {broken ? <div className="hp-splash-logo">PA</div> : <img src="/logo-hero.png" alt="Logo" className="hp-splash-img" onError={() => setBroken(true)} />}
      <div className="hp-splash-bar"><div className="hp-splash-fill" /></div>
    </div>
  );
};

/* ───── Firefly Canvas ───── */
const FireflyCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const isMobile = window.innerWidth < 768;
    const count = isMobile ? 15 : 40;
    let animId;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const flies = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      r: 1.5 + Math.random() * 2.5, dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.3, phase: Math.random() * Math.PI * 2,
      speed: 0.01 + Math.random() * 0.02
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      flies.forEach(f => {
        f.phase += f.speed;
        f.x += f.dx + Math.sin(f.phase) * 0.3;
        f.y += f.dy + Math.cos(f.phase * 0.7) * 0.2;
        if (f.x < 0) f.x = canvas.width; if (f.x > canvas.width) f.x = 0;
        if (f.y < 0) f.y = canvas.height; if (f.y > canvas.height) f.y = 0;
        const alpha = 0.2 + 0.6 * ((Math.sin(f.phase * 2) + 1) / 2);
        ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(244, 208, 63, ${alpha})`; ctx.fill();
        ctx.beginPath(); ctx.arc(f.x, f.y, f.r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(244, 208, 63, ${alpha * 0.15})`; ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} className="hp-fireflies" />;
};

/* ───── Animated Counter ───── */
const Counter = ({ end, suffix = '', duration = 2000 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          setVal(Math.round(end * p));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

/* ───── ScrollReveal wrapper ───── */
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.classList.add('hp-visible'); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('hp-visible'); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`hp-reveal ${className}`} style={{ transitionDelay: `${delay}s` }}>{children}</div>;
};

/* ───── Wave Separator ───── */
const Wave = ({ color = '#F5EDD6', flip = false }) => (
  <div className={`hp-wave ${flip ? 'hp-wave-flip' : ''}`}>
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
      <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,20 1440,40 L1440,80 L0,80 Z" fill={color} />
    </svg>
  </div>
);

/* ───── Danzas Slider ───── */
const DanzasSlider = ({ items }) => {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const checkScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 10);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = trackRef.current;
    if (el) el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => { el?.removeEventListener('scroll', checkScroll); window.removeEventListener('resize', checkScroll); };
  }, [items]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = el.querySelector('.hp-danza-card')?.offsetWidth || 300;
    el.scrollBy({ left: dir * (cardW + 28), behavior: 'smooth' });
  };

  return (
    <div className="hp-slider-wrap" data-testid="danzas-slider">
      {canPrev && (
        <button className="hp-slider-btn hp-slider-prev" onClick={() => scroll(-1)} data-testid="slider-prev">
          <ChevronLeft size={24} />
        </button>
      )}
      <div className="hp-slider-track" ref={trackRef}>
        {items.map((d, i) => (
          <div key={d.id || i} className="hp-danza-card" data-testid={`danza-card-${i}`}>
            <div className="hp-danza-img" style={d.imagen_url ? { backgroundImage: `url(${d.imagen_url})` } : {}} />
            <h3 className="hp-danza-name">{d.nombre}</h3>
            <p className="hp-danza-desc">{d.descripcion}</p>
          </div>
        ))}
      </div>
      {canNext && (
        <button className="hp-slider-btn hp-slider-next" onClick={() => scroll(1)} data-testid="slider-next">
          <ChevronRight size={24} />
        </button>
      )}
    </div>
  );
};

/* ───── Contact Drawer ───── */
const ContactDrawer = ({ open, onClose, info }) => {
  const [logoBroken, setLogoBroken] = useState(false);
  const drawerLogo = info?.logo_url || '/logo-pichiroces-nofondo.svg';
  return (
  <div className={`hp-drawer-overlay ${open ? 'hp-drawer-open' : ''}`} onClick={onClose}>
    <div className={`hp-drawer ${open ? 'hp-drawer-slide' : ''}`} onClick={e => e.stopPropagation()} data-testid="contact-drawer">
      <button onClick={onClose} className="hp-drawer-close" data-testid="close-drawer"><X size={20} /></button>
      {logoBroken ? <div className="hp-drawer-logo">PA</div> : <img src="/logo-small.png" alt="Logo" className="hp-drawer-logo-img" onError={() => setLogoBroken(true)} />}
      <h3 className="hp-drawer-title">Pichiroces Amazónicos</h3>
      <p className="hp-drawer-sub">Fraternidad de Folklore Boliviano — Beni, Bolivia</p>
      <div className="hp-drawer-sep" />
      <div className="hp-drawer-section">
        <h4>Contacto Directo</h4>
        {info?.contacto?.email && <a href={`mailto:${info.contacto.email}`} className="hp-drawer-row"><Mail size={16} />{info.contacto.email}</a>}
        {info?.contacto?.whatsapp && <a href={`https://wa.me/${info.contacto.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hp-drawer-row hp-wa"><Phone size={16} />{info.contacto.whatsapp}</a>}
        {info?.contacto?.ubicacion && <span className="hp-drawer-row"><MapPin size={16} />{info.contacto.ubicacion}</span>}
      </div>
      <div className="hp-drawer-section">
        <h4>Redes Sociales</h4>
        <div className="hp-drawer-socials">
          {info?.redes_sociales?.instagram && <a href={info.redes_sociales.instagram} target="_blank" rel="noopener noreferrer" className="hp-social-btn">Instagram</a>}
          {info?.redes_sociales?.facebook && <a href={info.redes_sociales.facebook} target="_blank" rel="noopener noreferrer" className="hp-social-btn">Facebook</a>}
          {info?.redes_sociales?.tiktok && <a href={info.redes_sociales.tiktok} target="_blank" rel="noopener noreferrer" className="hp-social-btn">TikTok</a>}
          {info?.redes_sociales?.youtube && <a href={info.redes_sociales.youtube} target="_blank" rel="noopener noreferrer" className="hp-social-btn">YouTube</a>}
          {!info?.redes_sociales?.instagram && !info?.redes_sociales?.facebook && <p className="hp-drawer-empty">Próximamente</p>}
        </div>
      </div>
    </div>
  </div>
  );
};

/* ══════════════════════════════════════ */
/*          MAIN HOMEPAGE                 */
/* ══════════════════════════════════════ */
export default function Homepage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [danzas, setDanzas] = useState([]);
  const [porque, setPorque] = useState(null);
  const [noticias, setNoticias] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [navSolid, setNavSolid] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [selectedNoticia, setSelectedNoticia] = useState(null);
  const heroRef = useRef(null);
  const sessionIdRef = useRef('');
  const jaguarAudioRef = useRef(null);

  useEffect(() => {
    let sid = sessionStorage.getItem('pichiroces_session');
    if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now(); sessionStorage.setItem('pichiroces_session', sid); }
    sessionIdRef.current = sid;
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('pichiroces_splash_shown')) {
      setShowSplash(true);
      localStorage.setItem('pichiroces_splash_shown', 'true');
    }
  }, []);

  useEffect(() => {
    const base = API || '';
    Promise.all([
      fetch(`${base}/api/public/info`).then(r => r.json()),
      fetch(`${base}/api/public/danzas`).then(r => r.json()),
      fetch(`${base}/api/public/seccion-porque`).then(r => r.json()),
      fetch(`${base}/api/public/noticias?limit=6`).then(r => r.json())
    ]).then(([i, d, p, n]) => {
      setInfo(i); setDanzas(d); setPorque(p); setNoticias(n.noticias || []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const handler = () => setNavSolid(window.scrollY > 80);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.innerWidth < 768) return;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (heroRef.current) heroRef.current.style.transform = `translateY(${window.scrollY * 0.35}px)`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  const scrollTo = (id) => { setMobileNav(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const stats = info?.stats || { anios: 5, integrantes: 30, actuaciones: 50, paises: 3 };
  const quienes = info?.quienes_somos || {};
  const pilarIcons = { leaf: Leaf, bird: Bird, handshake: Handshake };
  const logoUrl = info?.logo_url || '/logo-pichiroces-nofondo.svg';
  const logoSmall = '/logo-small.png';
  const logoHero = '/logo-hero.png';
  const LogoImg = ({ className, alt, size = 'small' }) => {
    const [broken, setBroken] = useState(false);
    const src = size === 'hero' ? logoHero : logoSmall;
    if (broken) return <span className="hp-logo-text">PA</span>;
    return <img src={src} alt={alt || 'Pichiroces Amazónicos'} className={className} onError={() => setBroken(true)} />;
  };

  const playJaguarRoar = () => {
    try {
      if (!jaguarAudioRef.current) {
        jaguarAudioRef.current = new Audio('/sounds/jaguar-roar.mp3');
        jaguarAudioRef.current.volume = 0.7;
      }
      jaguarAudioRef.current.currentTime = 0;
      jaguarAudioRef.current.play().catch(() => {});
    } catch {}
  };

  if (showSplash) return <SplashScreen onDone={() => setShowSplash(false)} />;

  return (
    <div className="hp-root" data-testid="homepage">
      {/* ─── NAVBAR ─── */}
      <nav className={`hp-nav ${navSolid ? 'hp-nav-solid' : ''}`} data-testid="homepage-nav">
        <div className="hp-nav-inner">
          <a href="/" className="hp-nav-logo">
            <LogoImg className="hp-nav-logo-img" alt="Pichiroces" />
          </a>
          <div className="hp-nav-links">
            <button onClick={() => scrollTo('hero')}>Inicio</button>
            <button onClick={() => scrollTo('quienes')}>Quiénes Somos</button>
            <button onClick={() => scrollTo('danzas')}>Qué Hacemos</button>
            <button onClick={() => scrollTo('noticias')}>Noticias</button>
            <button onClick={() => setDrawerOpen(true)}>Contacto</button>
          </div>
          <button onClick={() => navigate('/login')} className="hp-nav-cta" data-testid="nav-login-btn">Acceso Socios &rarr;</button>
          <button className="hp-nav-burger" onClick={() => setMobileNav(!mobileNav)} data-testid="mobile-nav-toggle"><Menu size={24} /></button>
        </div>
        {mobileNav && (
          <div className="hp-mobile-menu" data-testid="mobile-menu">
            <button onClick={() => scrollTo('hero')}>Inicio</button>
            <button onClick={() => scrollTo('quienes')}>Quiénes Somos</button>
            <button onClick={() => scrollTo('danzas')}>Qué Hacemos</button>
            <button onClick={() => scrollTo('noticias')}>Noticias</button>
            <button onClick={() => { setMobileNav(false); setDrawerOpen(true); }}>Contacto</button>
            <button onClick={() => { setMobileNav(false); navigate('/login'); }} className="hp-mobile-cta">Acceso Socios</button>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section id="hero" className="hp-hero" data-testid="hero-section">
        <div className="hp-hero-bg" ref={heroRef} />
        <div className="hp-hero-grain" />
        <FireflyCanvas />
        <div className="hp-hero-content">
          <span className="hp-hero-label hp-stagger-1">DEPARTAMENTO DEL BENI &middot; BOLIVIA</span>
          <div onClick={playJaguarRoar} className="hp-hero-logo-wrap hp-stagger-2" style={{ cursor: 'pointer' }} data-testid="hero-logo-roar" title="Haz click para escuchar el rugido del jaguar">
            <LogoImg className="hp-hero-logo" alt="Pichiroces Amazónicos" size="hero" />
          </div>
          <p className="hp-hero-sub hp-stagger-3">Fraternidad de Folklore Boliviano</p>
          <div className="hp-hero-line hp-stagger-4" />
          <p className="hp-hero-desc hp-stagger-5">
            {info?.descripcion || 'Llevamos la riqueza cultural del Oriente Boliviano a cada rincón del mundo, con orgullo, identidad y pasión.'}
          </p>
          <div className="hp-hero-btns hp-stagger-6">
            <button onClick={() => scrollTo('quienes')} className="hp-btn-primary" data-testid="hero-cta-historia">Conoce nuestra historia</button>
            <button onClick={() => setDrawerOpen(true)} className="hp-btn-outline" data-testid="hero-cta-unirse">Quiero unirme &rarr;</button>
          </div>
        </div>
        <div className="hp-scroll-indicator"><ChevronDown size={24} /></div>
      </section>

      {/* ─── QUIÉNES SOMOS ─── */}
      <Wave color="#F5EDD6" />
      <section id="quienes" className="hp-section hp-section-cream" data-testid="quienes-section">
        <div className="hp-container hp-two-col">
          <div className="hp-col-text">
            <Reveal><span className="hp-label">NUESTRA IDENTIDAD</span></Reveal>
            <Reveal delay={0.1}><h2 className="hp-h2">Raíces que danzan en la Amazonia</h2></Reveal>
            <Reveal delay={0.2}>
              <p className="hp-body">
                {quienes.parrafo1 || 'Somos la Fraternidad Pichiroces Amazónicos, una familia unida por el amor al folklore del Departamento del Beni, en el corazón del oriente boliviano. Nuestro nombre nace de la palabra «Pichiró», esa expresión beniana que describe a la fruta en su punto entre verde y maduro — como nosotros, que vivimos entre la fuerza de la juventud y la experiencia que nos vuelve más sabios.'}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="hp-body">
                {quienes.parrafo2 || 'Fundada por benianos orgullosos de sus raíces, la fraternidad nació con un sueño: preservar, celebrar y compartir las danzas, músicas y tradiciones del Beni con el mundo entero.'}
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="hp-stats" data-testid="stats-section">
                <div className="hp-stat"><span className="hp-stat-num"><Counter end={stats.anios} suffix="+" /></span><span className="hp-stat-label">Años</span></div>
                <div className="hp-stat"><span className="hp-stat-num"><Counter end={stats.integrantes} /></span><span className="hp-stat-label">Integrantes</span></div>
                <div className="hp-stat"><span className="hp-stat-num"><Counter end={stats.actuaciones} suffix="+" /></span><span className="hp-stat-label">Actuaciones</span></div>
                <div className="hp-stat"><span className="hp-stat-num"><Counter end={stats.paises} /></span><span className="hp-stat-label">Países</span></div>
              </div>
            </Reveal>
          </div>
          <Reveal className="hp-col-visual" delay={0.2}>
            <LogoImg className="hp-brand-img" alt="Pichiroces Amazónicos" size="hero" />
          </Reveal>
        </div>
      </section>

      {/* ─── QUÉ HACEMOS (SLIDER) ─── */}
      <Wave color="#0A0F0D" flip />
      <section id="danzas" className="hp-section hp-section-dark" data-testid="danzas-section">
        <div className="hp-container">
          <Reveal><span className="hp-label hp-label-light">NUESTRA EXPRESIÓN</span></Reveal>
          <Reveal delay={0.1}><h2 className="hp-h2 hp-h2-light">El folklore del Beni en cada paso</h2></Reveal>
          <Reveal delay={0.2}><p className="hp-body hp-body-light">Las danzas y actividades que representamos son el alma viva del pueblo beniano</p></Reveal>
          <Reveal delay={0.3}>
            <DanzasSlider items={danzas} />
          </Reveal>
        </div>
      </section>

      {/* ─── POR QUÉ LO HACEMOS ─── */}
      <section className="hp-section hp-section-mission" data-testid="mission-section">
        <div className="hp-mission-grain" />
        <div className="hp-container hp-center">
          <Reveal><span className="hp-label hp-label-light">NUESTRA MISIÓN</span></Reveal>
          <Reveal delay={0.1}><h2 className="hp-h2 hp-h2-light hp-h2-big">{porque?.titulo || 'Preservar el alma del Beni'}</h2></Reveal>
          <Reveal delay={0.2}><p className="hp-body hp-body-light hp-body-narrow">{porque?.parrafo || ''}</p></Reveal>
          <div className="hp-pilares">
            {(porque?.pilares || []).map((p, i) => {
              const Icon = pilarIcons[p.icono] || Leaf;
              return (
                <Reveal key={i} delay={0.15 * i} className="hp-pilar">
                  <Icon className="hp-pilar-icon" size={32} />
                  <h4 className="hp-pilar-title">{p.titulo}</h4>
                  <p className="hp-pilar-desc">{p.descripcion}</p>
                </Reveal>
              );
            })}
          </div>
          {porque?.quote && (
            <Reveal delay={0.5}>
              <blockquote className="hp-quote">
                <p>"{porque.quote}"</p>
                <cite>— {porque.quote_author || 'Pichiroces Amazónicos'}</cite>
              </blockquote>
            </Reveal>
          )}
        </div>
      </section>

      {/* ─── NOTICIAS ─── */}
      <Wave color="#F8F5EE" />
      <section id="noticias" className="hp-section hp-section-bruma" data-testid="noticias-section">
        <div className="hp-container">
          <Reveal><span className="hp-label">ÚLTIMAS NOTICIAS</span></Reveal>
          <Reveal delay={0.1}><h2 className="hp-h2">Lo que está pasando en la fraternidad</h2></Reveal>
          {noticias.length > 0 ? (
            <div className="hp-noticias-grid">
              {noticias.map((n, i) => (
                <Reveal key={n.id || i} delay={0.1 * i} className={`hp-noticia-card ${i === 0 ? 'hp-noticia-featured' : ''}`}>
                  <div className="hp-noticia-img" style={n.image_url ? { backgroundImage: `url(${n.image_url.startsWith('/api') ? (API || '') + n.image_url : n.image_url})` } : {}} />
                  <div className="hp-noticia-body">
                    {n.category && <span className="hp-noticia-badge">{n.category}</span>}
                    <p className="hp-noticia-date">{n.created_at ? new Date(n.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</p>
                    <h3 className="hp-noticia-title">{n.title}</h3>
                    <p className="hp-noticia-excerpt">{n.content?.slice(0, 150)}...</p>
                    <button className="hp-noticia-link" onClick={() => setSelectedNoticia(n)}>Leer más &rarr;</button>
                    {/* Public Reactions */}
                    <div className="hp-reactions-bar" data-testid={`public-reactions-${n.id}`}>
                      {['👍','❤️','😂','🎉','🔥'].map(emoji => {
                        const count = (n.reactions_summary || {})[emoji] || 0;
                        return (
                          <button
                            key={emoji}
                            className="hp-reaction-btn"
                            data-testid={`public-react-${emoji}-${n.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const base = API || '';
                                const res = await fetch(`${base}/api/public/noticias/${n.id}/react`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ emoji, session_id: sessionIdRef.current })
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  setNoticias(prev => prev.map(item => item.id === n.id ? { ...item, reactions_summary: data.reactions_summary } : item));
                                }
                              } catch {}
                            }}
                          >
                            <span>{emoji}</span>
                            {count > 0 && <span className="hp-reaction-count">{count}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <Reveal><p className="hp-empty-state">Próximamente — Noticias de la fraternidad</p></Reveal>
          )}
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="hp-section hp-section-cta" data-testid="cta-section">
        <div className="hp-container hp-center">
          <Reveal><h2 className="hp-h2 hp-h2-light hp-h2-big">¿Sientes el llamado de la selva?</h2></Reveal>
          <Reveal delay={0.1}><p className="hp-body hp-body-light">Únete a la familia Pichiroco o apóyanos como colaborador</p></Reveal>
          <div className="hp-cta-cards">
            <Reveal delay={0.2} className="hp-cta-card">
              <Bird size={40} className="hp-cta-icon" />
              <h3>Soy boliviano/a o amante del folklore</h3>
              <p>Quiero bailar, participar y ser parte activa de la fraternidad</p>
              <button onClick={() => setDrawerOpen(true)} className="hp-btn-primary" data-testid="cta-unirse">Quiero unirme</button>
            </Reveal>
            <Reveal delay={0.3} className="hp-cta-card">
              <Handshake size={40} className="hp-cta-icon" />
              <h3>Tengo algo que aportar</h3>
              <p>Quiero apoyar a la fraternidad con mis servicios o patrocinio</p>
              <button onClick={() => setDrawerOpen(true)} className="hp-btn-primary" data-testid="cta-colaborar">Quiero colaborar</button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="hp-footer" data-testid="homepage-footer">
        <div className="hp-footer-inner">
          <div className="hp-footer-brand">
            <LogoImg className="hp-footer-logo-img" alt="Pichiroces" />
            <h4>Pichiroces Amazónicos</h4>
            <p>{info?.tagline_footer || 'Del corazón del Beni al mundo entero'}</p>
          </div>
          <div className="hp-footer-links">
            <button onClick={() => scrollTo('hero')}>Inicio</button>
            <button onClick={() => scrollTo('quienes')}>Quiénes Somos</button>
            <button onClick={() => scrollTo('danzas')}>Qué Hacemos</button>
            <button onClick={() => scrollTo('noticias')}>Noticias</button>
            <button onClick={() => setDrawerOpen(true)}>Contacto</button>
          </div>
          <div className="hp-footer-sep" />
          <div className="hp-footer-bottom">
            <p>&copy; {new Date().getFullYear()} Pichiroces Amazónicos — Todos los derechos reservados</p>
            <button onClick={() => navigate('/login')} className="hp-footer-login" data-testid="footer-login-btn">Acceso Socios</button>
          </div>
        </div>
      </footer>

      {/* ─── MODALS ─── */}
      <ContactDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} info={info} />
      {selectedNoticia && (
        <div className="hp-modal-overlay" onClick={() => setSelectedNoticia(null)} data-testid="noticia-modal">
          <div className="hp-modal" onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelectedNoticia(null)} className="hp-modal-close"><X size={20} /></button>
            <h2>{selectedNoticia.title}</h2>
            {selectedNoticia.category && <span className="hp-noticia-badge">{selectedNoticia.category}</span>}
            <p className="hp-modal-date">{selectedNoticia.created_at ? new Date(selectedNoticia.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</p>
            <div className="hp-modal-content">{selectedNoticia.content}</div>
          </div>
        </div>
      )}
    </div>
  );
}
